import React, { useState } from 'react';
import { WxLink } from '@watkinslabs/watkinsx-components';

// pulse-player bench/README.md: 1920×1080, headless Chrome, RTX 4090 through ANGLE/OpenGL ES 3.2.
// [step, cpu mean, cpu p95, frame mean, frame p95, draws, uploaded, uniforms, heap churn]
const STEPS = ['baseline', 'GPU geometry', 'batching', 'bitmap cache', 'zero allocation'];
const DATA = [
  {
    id: 'showcase', title: 'Showcase', link: 'https://eizo.watkinsx.com/watch/station/p882c974006d1ffa?track=zSTrz_IU2ZeqTXRts7dDlw', what: 'Kernel Space: 2016 bezier paths, kinetic type, 400 particle breaks, widgets',
    rows: [
      [3.2, 7.0, 3.47, 7.3, 267, '754.4 kB', 2426, '6.1 MB'],
      [1.12, 2.1, 1.52, 2.7, 267, '232.6 kB', 1601, '1000.0 kB'],
      [0.93, 1.8, 1.29, 2.3, 45, '232.6 kB', 246, '920.6 kB'],
      [0.9, 1.7, 1.25, 2.1, 45, '232.6 kB', 246, '918.0 kB'],
      [0.82, 1.3, 1.21, 1.9, 45, '230.5 kB', 245, '226.4 kB'],
    ],
  },
  {
    id: 'traced', title: 'Traced image', what: 'a PNG traced into 20 colour layers: 2010 paths, 42 746 points',
    rows: [
      [31.19, 42.5, 31.57, 42.9, 42, '16.1 MB', 382, '114.6 MB'],
      [0.13, 0.2, 1.25, 1.4, 42, '0 B', 197, '44.8 kB'],
      [0.13, 0.2, 1.27, 1.4, 42, '0 B', 193, '43.0 kB'],
      [0.13, 0.2, 1.28, 1.4, 42, '0 B', 193, '44.2 kB'],
      [0.13, 0.2, 1.29, 1.4, 42, '0 B', 193, '30.7 kB'],
    ],
  },
  {
    id: 'stress', title: 'Governor stress', what: '100 stroked stars, 6000 particles, widgets, blur, bloom + feedback',
    rows: [
      [9.36, 10.5, 11.23, 12.4, 505, '2.4 MB', 4653, '17.1 MB'],
      [6.51, 8.3, 8.5, 10.6, 505, '2.4 MB', 2550, '12.1 MB'],
      [6.1, 7.5, 8.01, 9.4, 88, '2.4 MB', 600, '12.0 MB'],
      [6.31, 8.0, 8.2, 10.1, 88, '2.4 MB', 600, '11.9 MB'],
      [2.36, 2.6, 4.23, 4.5, 88, '555.7 kB', 595, '911.9 kB'],
    ],
  },
];

function Panel({ s }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(...s.rows.map((r) => r[3]));
  const first = s.rows[0][2], last = s.rows[4][2];
  return (
    <figure className="bench-panel">
      <figcaption>
        <b>{s.title}</b>
        <span className="dim small">{s.what}{s.link && <> · <WxLink href={s.link}>watch it</WxLink></>}</span>
      </figcaption>
      <p className="bench-head"><b>{(first / last).toFixed(1)}×</b> <span className="dim">faster frame, {first.toFixed(2)} → {last.toFixed(2)} ms</span></p>
      <div className="bench-bars" onMouseLeave={() => setHover(null)}>
        {s.rows.map((r, i) => (
          <div
            className={`bench-row ${hover === i ? 'is-hover' : ''}`} key={i}
            onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} tabIndex={0}
            aria-label={`${STEPS[i]}: frame mean ${r[2]} ms, p95 ${r[3]} ms, ${r[4]} draws`}
          >
            <span className="bench-step">{STEPS[i]}</span>
            <span className="bench-track">
              <span className="bench-fill" style={{ width: `${r[2] / max * 100}%` }} />
              <span className="bench-p95" style={{ left: `${r[3] / max * 100}%` }} />
            </span>
            <span className="bench-val">{r[2].toFixed(2)}</span>
          </div>
        ))}
        {hover != null && (
          <div className="bench-tip" style={{ top: `${hover * 30 + 30}px` }} role="tooltip">
            <b>{STEPS[hover]}</b>
            <span>frame {s.rows[hover][2]} ms mean · {s.rows[hover][3]} ms p95</span>
            <span>CPU {s.rows[hover][0]} ms mean · {s.rows[hover][1]} ms p95</span>
            <span>{s.rows[hover][4]} draws · {s.rows[hover][6]} uniform calls</span>
            <span>{s.rows[hover][5]} uploaded · {s.rows[hover][7]} heap churn</span>
          </div>
        )}
      </div>
      <p className="bench-axis dim small">frame time, ms (bar: mean, tick: p95). Scales differ per panel.</p>
    </figure>
  );
}

export default function BenchSection() {
  return (
    <section className="sec" id="bench" aria-labelledby="bench-h">
      <div className="wrap">
        <header className="sec-head">
          <p className="kicker">Benchmarks</p>
          <h2 id="bench-h">Four renderer steps, measured one at a time.</h2>
          <p className="sec-lead">
            <code>npm run bench</code> renders fixed times of three scenes at 1920×1080 in headless Chrome and
            times each frame including a readback, so the GPU has finished. The steps are cumulative; GPU
            geometry, batching and zero allocation were each checked pixel-identical on every sampled frame. Machine: RTX 4090 through ANGLE/OpenGL ES 3.2.
          </p>
        </header>
        <div className="bench-grid">
          {DATA.map((s) => <Panel s={s} key={s.id} />)}
        </div>
        <details className="bench-table">
          <summary>The full table</summary>
          <div className="tbl-scroll" tabIndex={0} role="region" aria-label="Benchmark table">
            <table className="wx-table tbl">
              <thead><tr><th>scene</th><th>step</th><th>cpu mean</th><th>cpu p95</th><th>frame mean</th><th>frame p95</th><th>draws</th><th>uploaded</th><th>uniforms</th><th>heap churn</th></tr></thead>
              <tbody>
                {DATA.flatMap((s) => s.rows.map((r, i) => (
                  <tr key={s.id + i}><td>{i === 0 ? s.title : ''}</td><td>{i}-{STEPS[i]}</td><td>{r[0]} ms</td><td>{r[1]} ms</td><td>{r[2]} ms</td><td>{r[3]} ms</td><td>{r[4]}</td><td>{r[5]}</td><td>{r[6]}</td><td>{r[7]}</td></tr>
                )))}
              </tbody>
            </table>
          </div>
          <p className="dim small">Source: pulse-player <code>bench/README.md</code>. Run-to-run noise is about ±5% on CPU and frame times.</p>
        </details>
      </div>
    </section>
  );
}
